import { showAlert } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useUnAuthStackNavigation } from '@/hooks';
import { signInWithEmailAndPassword, signOut } from '@/libs/firebase/auth';
import { captureException } from '@/libs/logger';
import { storage } from '@/libs/storage';
import { UnAuthStackParamList } from '@/navigations/types';
import { useUiStore } from '@/stores';
import { sendEmailVerification } from '@react-native-firebase/auth';
import { RouteProp, useRoute } from '@react-navigation/native';
import { useCallback } from 'react';
import { useShallow } from 'zustand/shallow';

/** Only re-send a verification email once per hour; within the window we just re-show the notice. */
const VERIFICATION_EMAIL_COOLDOWN_MS = 60 * 60 * 1000;
const getLastSentStorageKey = (email: string) => `verify-email:last-sent:${email}`;

export function useVerifyEmail() {
  const navigation = useUnAuthStackNavigation();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const params = useRoute<RouteProp<UnAuthStackParamList, 'VerifyEmail'>>().params;
  const email = params?.email ?? '';
  const defaultPass = params?.defaultPass ?? '';

  const handleCancel = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const showSuccessNotice = useCallback(() => {
    showAlert({
      title: STRINGS.auth.verifyEmail.noticeTitle,
      message: STRINGS.auth.verifyEmail.successMessage,
      buttons: [{ text: STRINGS.dismiss, onPress: handleCancel }],
    });
  }, [handleCancel]);

  const handleSendVerificationEmail = useCallback(async () => {
    const storageKey = getLastSentStorageKey(email);
    const lastSentAt = Number(storage.getString(storageKey)) || 0;
    const now = Date.now();

    // Within the cooldown window: skip the network call, just re-show the notice.
    if (lastSentAt && now - lastSentAt < VERIFICATION_EMAIL_COOLDOWN_MS) {
      showSuccessNotice();
      return;
    }

    try {
      showGlobalLoading();
      const authUser = await signInWithEmailAndPassword(email, defaultPass);
      await sendEmailVerification(authUser.user);
      storage.set(storageKey, String(now));
      showSuccessNotice();
      signOut();
    } catch (error) {
      captureException(
        {
          error_payload: error,
        },
        { tags: { usk_log_type: 'handle_send_verification_email' } },
      );
      showAlert({
        title: STRINGS.auth.verifyEmail.noticeTitle,
        message: STRINGS.auth.verifyEmail.errorSendVerificationEmail,
      });
    } finally {
      hideGlobalLoading();
    }
  }, [defaultPass, email, hideGlobalLoading, showGlobalLoading, showSuccessNotice]);

  return {
    email,
    handleSendVerificationEmail,
    handleCancel,
  };
}
